import React, { useEffect } from 'react';
import { CheckCircle2, Share2, Trophy, Film, Sparkles, X, Copy } from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';

export interface ShareConfirmationData {
  type: 'highlight' | 'leaderboard_rank' | 'game_boxscore' | 'comparison';
  title: string;
  subtitle?: string;
  sport?: string;
  imageUrl?: string;
  xpAwarded?: number;
}

interface ShareConfirmationNotificationProps {
  data: ShareConfirmationData | null;
  onClose: () => void;
  autoDismissMs?: number;
}

export const ShareConfirmationNotification: React.FC<ShareConfirmationNotificationProps> = ({
  data,
  onClose,
  autoDismissMs = 4500,
}) => {
  useEffect(() => {
    if (data) {
      triggerHaptic('success');
      const timer = setTimeout(() => {
        onClose();
      }, autoDismissMs);
      return () => clearTimeout(timer);
    }
  }, [data, autoDismissMs, onClose]);

  if (!data) return null;

  const isLeaderboard = data.type === 'leaderboard_rank' || data.type === 'comparison';

  return (
    <div className="fixed top-6 right-6 z-50 max-w-md w-full px-4 animate-fadeIn pointer-events-auto">
      <div className="bg-indigo-950/95 backdrop-blur-xl border-2 border-lime-400 p-4 rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.6)] text-white relative flex items-start space-x-3.5 overflow-hidden group">
        {/* Shimmer Background Beam */}
        <div className="absolute inset-0 bg-gradient-to-r from-lime-400/10 via-transparent to-sky-400/10 opacity-60 pointer-events-none" />

        {/* Icon Emblem Badge */}
        <div className="relative shrink-0 mt-0.5">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-lime-400 to-emerald-500 text-black flex items-center justify-center shadow-lg shadow-lime-400/30 font-black">
            {isLeaderboard ? (
              <Trophy className="w-6 h-6 stroke-[2.5]" />
            ) : (
              <Film className="w-6 h-6 stroke-[2.5]" />
            )}
          </div>
          <div className="absolute -bottom-1 -right-1 bg-black text-lime-400 p-0.5 rounded-full border border-lime-400">
            <CheckCircle2 className="w-4 h-4 fill-lime-400 text-black stroke-[2.5]" />
          </div>
        </div>

        {/* Content Body */}
        <div className="flex-1 min-w-0 pr-6 space-y-1">
          <div className="flex items-center space-x-2">
            <span className="px-2 py-0.5 rounded-md bg-lime-400 text-black font-mono font-black text-[10px] uppercase tracking-wider flex items-center gap-1">
              <Share2 className="w-3 h-3 stroke-[3]" />
              <span>{isLeaderboard ? 'Rank Card Shared ✓' : 'Highlight Reel Shared ✓'}</span>
            </span>

            {data.xpAwarded && (
              <span className="px-2 py-0.5 rounded-md bg-amber-400/20 text-amber-300 font-mono font-black text-[10px] uppercase border border-amber-400/30 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-400 fill-amber-400" />
                <span>+{data.xpAwarded} XP</span>
              </span>
            )}
          </div>

          <h4 className="text-sm font-black italic text-white truncate leading-snug">
            {data.title}
          </h4>

          <p className="text-xs text-indigo-200/90 font-medium leading-relaxed line-clamp-2">
            {data.subtitle || (isLeaderboard 
              ? 'Social rank card & stats generated. Share link copied to clipboard!' 
              : 'Highlight clip link & preview card copied! Ready to post on feeds.')}
          </p>

          <div className="pt-1 flex items-center space-x-2 text-[10px] font-mono text-lime-300 font-bold">
            <Copy className="w-3 h-3 text-lime-400" />
            <span>Link copied & broadcasted to social network</span>
          </div>
        </div>

        {/* Dismiss Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3.5 right-3.5 p-1.5 rounded-xl bg-white/5 hover:bg-white/15 text-indigo-200 hover:text-white transition"
          title="Dismiss confirmation"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
